/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { debounce } from "lodash";
import { useEffect, useMemo, useState } from "react";

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  // Create a debounced function using useMemo and lodash.debounce
  const debouncedSetValue = useMemo(
    () => debounce((val: T) => setDebouncedValue(val), delay),
    [delay]
  );

  useEffect(() => {
    // Call the debounced function with the latest value
    debouncedSetValue(value);

    // Cancel the debounced function on component unmount or when dependencies change
    return () => {
      debouncedSetValue.cancel();
    };
  }, [value, debouncedSetValue]);

  return debouncedValue;
}

export default useDebounce;
